import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Phone, Mail, MapPin, Calendar, Crown, Flag, Clock, FileText, UserPlus, Pencil, ArrowUp, Building2, MessageSquare, Edit2, Trash2, Activity, X, Plus, Paperclip, Image, Download } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { Card, Badge, Spinner, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Select, Textarea, SlidePanel } from '../../../components/ui'
import { toast } from '../../../components/Toast'
import { maskCEP, unmaskCEP, unmaskCPF, unmaskPhone } from '../../../utils/masks'
import { formatPhone, maskCPF } from '../../../utils/masks'
import { formatDate, formatDateTime, formatRelativeDate } from '../../../utils/dateUtils'
import RegisterActivityModal from '../../../components/RegisterActivityModal'

interface VoterData {
  id: number; name: string; nickname: string | null; cpf: string | null; sus_number: string | null
  phone: string; email: string | null; birth_date: string | null; gender: string | null
  is_contact: boolean; is_voter: boolean
  zip_code: string | null; street: string | null; number: string | null
  neighborhood: string | null; complement: string | null
  city_name: string | null; state_code: string | null; neighborhood_name: string | null
  channel_label: string | null; form_name: string | null; leader_name: string | null
  family_group_name: string | null
  voter_registration: string | null; tse_zone_id: number | null; tse_section_id: number | null
  electoral_zone: string | null; electoral_section: string | null
  electoral_location: string | null; electoral_address: string | null
  electoral_neighborhood: string | null; electoral_city: string | null
  electoral_voters: number | null
  status_code: string; status_label: string
  notes: string | null; created_at: string
}

interface HistoryAttachment {
  id: number; file_url: string; file_name: string; file_size: number; mime_type: string | null
}

interface HistoryItem {
  id: number; description: string | null; action_code: string; action_label: string
  action_icon: string; action_color: string
  old_type: string | null; new_type: string | null
  user_name: string | null; leader_name: string | null; channel_label: string | null
  attachments: HistoryAttachment[]
  created_at: string
}

interface CampaignItem {
  id: number; name: string; election_year: number; is_active_campaign: boolean
  status_label: string; status_color: string
  engagement_active: boolean; engaged_at: string
}

const iconMap: Record<string, React.ReactNode> = {
  UserPlus: <UserPlus className="w-3.5 h-3.5" />,
  Pencil: <Pencil className="w-3.5 h-3.5" />,
  ArrowUp: <ArrowUp className="w-3.5 h-3.5" />,
  Building2: <Building2 className="w-3.5 h-3.5" />,
  Phone: <Phone className="w-3.5 h-3.5" />,
  Flag: <Flag className="w-3.5 h-3.5" />,
  MessageSquare: <MessageSquare className="w-3.5 h-3.5" />,
}

export default function VoterDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [voter, setVoter] = useState<VoterData | null>(null)
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [campaigns, setCampaigns] = useState<CampaignItem[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('resumo')
  const [editOpen, setEditOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [activityModalOpen, setActivityModalOpen] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({
    name: '', nickname: '', phone: '', email: '', cpf: '',
    birth_date: '', gender: '',
    voter_registration: '', electoral_zone: '', electoral_section: '',
    zip_code: '', street: '', number: '', neighborhood: '', complement: '',
    notes: '',
  })

  useEffect(() => { if (id) fetchVoter() }, [id])

  async function fetchVoter() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl(`/v1/voters/${id}`))
      setVoter(res.data.data.voter)
      setHistory(res.data.data.history || [])
      setCampaigns(res.data.data.campaigns || [])
    } catch { /* */ } finally { setLoading(false) }
  }

  function openEdit() {
    if (!voter) return
    setEditForm({
      name: voter.name || '', nickname: voter.nickname || '',
      phone: voter.phone || '', email: voter.email || '', cpf: voter.cpf || '',
      birth_date: voter.birth_date || '', gender: voter.gender || '',
      voter_registration: voter.voter_registration || '',
      electoral_zone: voter.electoral_zone || '', electoral_section: voter.electoral_section || '',
      zip_code: voter.zip_code || '', street: voter.street || '',
      number: voter.number || '', neighborhood: voter.neighborhood || '',
      complement: voter.complement || '', notes: voter.notes || '',
    })
    setEditOpen(true)
  }

  async function handleSave() {
    if (saving) return
    setSaving(true)
    try {
      await axios.put(buildApiUrl(`/v1/voters/${id}`), {
        ...editForm,
        phone: unmaskPhone(editForm.phone) || editForm.phone,
        cpf: unmaskCPF(editForm.cpf) || editForm.cpf,
        zip_code: unmaskCEP(editForm.zip_code) || editForm.zip_code,
      })
      toast('success', 'Dados atualizados')
      setEditOpen(false)
      fetchVoter()
    } catch {
      toast('error', 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  async function handleDelete() {
    if (deleting) return
    setDeleting(true)
    try {
      await axios.delete(buildApiUrl(`/v1/voters/${id}`))
      toast('success', voter?.is_voter ? 'Eleitor desativado' : 'Contato desativado')
      navigate(voter?.is_voter ? '/voters' : '/contacts')
    } catch {
      toast('error', 'Erro ao desativar')
    } finally { setDeleting(false) }
  }

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-gray-200" />
        <div>
          <div className="h-4 w-16 bg-gray-200 rounded mb-2" />
          <div className="h-7 w-48 bg-gray-200 rounded" />
        </div>
      </div>
      <div className="flex gap-6 border-b border-gray-200">
        <div className="h-9 w-20 bg-gray-200 rounded-md" />
        <div className="h-9 w-28 bg-gray-200 rounded-md" />
        <div className="h-9 w-28 bg-gray-200 rounded-md" />
      </div>
      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-4 pb-6 border-b border-gray-100">
          {[1,2,3,4,5,6].map(i => (
            <div key={i}>
              <div className="h-3 w-16 bg-gray-200 rounded mb-2" />
              <div className="h-4 w-32 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
        <div className="py-6 border-b border-gray-100">
          <div className="h-3 w-20 bg-gray-200 rounded mb-3" />
          <div className="h-4 w-64 bg-gray-200 rounded" />
        </div>
        <div className="py-6">
          <div className="h-3 w-28 bg-gray-200 rounded mb-3" />
          <div className="flex gap-6">
            <div className="h-4 w-24 bg-gray-200 rounded" />
            <div className="h-4 w-16 bg-gray-200 rounded" />
            <div className="h-4 w-16 bg-gray-200 rounded" />
          </div>
        </div>
      </Card>
    </div>
  )
  if (!voter) return <div className="text-center py-20 text-gray-500">Registro não encontrado</div>

  const goBack = () => navigate(voter.is_voter ? '/voters' : '/contacts')
  const address = [voter.street, voter.number].filter(Boolean).join(', ')
  const neighborhood = voter.neighborhood_name || voter.neighborhood
  const cityState = voter.city_name ? `${voter.city_name}/${voter.state_code}` : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{voter.is_voter ? 'Eleitor' : 'Contato'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openEdit}>
            <Edit2 className="w-4 h-4 mr-1.5" />Editar
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDeleteModalOpen(true)} className="text-error-500 hover:bg-error-50 border-error-200">
            <Trash2 className="w-4 h-4 mr-1.5" />Excluir
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 border-b border-gray-200">
        {[
          { id: 'resumo', label: 'Dados' },
          { id: 'historico', label: `Atividade${history.length ? ` (${history.length})` : ''}` },
          { id: 'campanhas', label: `Campanhas${campaigns.length ? ` (${campaigns.length})` : ''}` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={clsx('pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px', activeTab === tab.id ? 'text-brand-600 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700')}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dados */}
      {activeTab === 'resumo' && (<>
        <Card>
          {/* Info principal */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-4 pb-6 border-b border-gray-100">
            <Field label="Nome" value={voter.name} />
            <Field label="Apelido" value={voter.nickname || '—'} muted={!voter.nickname} />
            <Field label="Telefone" value={formatPhone(voter.phone)} />
            {voter.email ? <Field label="Email" value={voter.email} /> : <Field label="Email" value="—" muted />}
            {voter.cpf ? <Field label="CPF" value={maskCPF(voter.cpf)} /> : <Field label="CPF" value="—" muted />}
            {voter.sus_number && <Field label="Cartão SUS" value={voter.sus_number} />}
            {voter.birth_date ? <Field label="Nascimento" value={formatDate(voter.birth_date)} /> : <Field label="Nascimento" value="—" muted />}
            {voter.gender && <Field label="Gênero" value={voter.gender === 'M' ? 'Masculino' : voter.gender === 'F' ? 'Feminino' : 'Outro'} />}
          </div>

          {/* Endereço */}
          <div className="py-6 border-b border-gray-100">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Endereço</h4>
            {address || neighborhood || cityState ? (
              <div>
                <p className="text-sm text-gray-900">
                  {[address, voter.complement, voter.zip_code && `CEP ${voter.zip_code}`].filter(Boolean).join(' - ')}
                </p>
                {neighborhood && <p className="text-sm text-gray-700 mt-0.5">{neighborhood}</p>}
                {cityState && <p className="text-sm text-gray-500 mt-0.5">{cityState}</p>}
              </div>
            ) : (
              <p className="text-sm text-gray-300">—</p>
            )}
          </div>

          {/* Dados Eleitorais */}
          <div className="py-6 border-b border-gray-100">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Dados Eleitorais</h4>
            {voter.voter_registration || voter.tse_zone_id ? (
              <div className="space-y-3">
                <div className="flex gap-6">
                  <Field label="Título" value={voter.voter_registration || '—'} muted={!voter.voter_registration} />
                  <Field label="Zona" value={voter.electoral_zone || '—'} muted={!voter.electoral_zone} />
                  <Field label="Seção" value={voter.electoral_section || '—'} muted={!voter.electoral_section} />
                </div>
                {voter.electoral_location && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">{voter.electoral_location}</p>
                    {voter.electoral_address && <p className="text-xs text-gray-500">{voter.electoral_address}</p>}
                    {voter.electoral_neighborhood && <p className="text-xs text-gray-500">{voter.electoral_neighborhood}{voter.electoral_city ? ` — ${voter.electoral_city}` : ''}</p>}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-300">—</p>
            )}
          </div>

          {/* Observações */}
          <div className="pt-6">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Observações</h4>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{voter.notes || '—'}</p>
          </div>
        </Card>

        {/* Origem - card separado */}
        <Card>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Origem</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-4">
            <Field label="Canal" value={voter.channel_label || '—'} muted={!voter.channel_label} />
            {voter.form_name && <Field label="Formulário" value={voter.form_name} />}
            {voter.leader_name && <Field label="Liderança" value={voter.leader_name} />}
            {voter.family_group_name && <Field label="Grupo familiar" value={voter.family_group_name} />}
            <Field label="Cadastrado em" value={formatDateTime(voter.created_at)} />
          </div>
        </Card>
      </>)}

      {/* Atividade / Histórico */}
      {activeTab === 'historico' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Atividade</h3>
            <Button size="sm" variant="outline" onClick={() => setActivityModalOpen(true)} icon={<Plus className="w-3.5 h-3.5" />}>Registrar</Button>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">Nenhuma atividade registrada</p>
          ) : (
            <div className="relative pl-6">
              <div className="absolute left-[11px] top-2 bottom-2 w-px bg-brand-100" />
              <div className="space-y-0">
                {history.map((h, i) => (
                  <div key={h.id} className="relative flex gap-4 py-4 first:pt-0 last:pb-0">
                    {/* Dot */}
                    <div className="absolute -left-6 top-4 w-[22px] h-[22px] rounded-full border-2 border-white bg-brand-500 flex items-center justify-center">
                      <span className="text-white">{iconMap[h.action_icon] || <div className="w-2 h-2 rounded-full bg-white" />}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 ml-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{h.action_label}</p>
                          {h.description && <p className="text-sm text-gray-600 mt-0.5">{h.description}</p>}
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap mt-0.5">{formatRelativeDate(h.created_at)}</span>
                      </div>
                      {(h.user_name || h.leader_name || h.channel_label) && (
                        <div className="flex flex-wrap gap-3 mt-1.5">
                          {h.user_name && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              por {h.user_name}
                            </span>
                          )}
                          {h.leader_name && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              <Crown className="w-3 h-3" /> {h.leader_name}
                            </span>
                          )}
                          {h.channel_label && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              {h.channel_label}
                            </span>
                          )}
                        </div>
                      )}
                      {h.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {h.attachments.map(att => {
                            const isImage = att.mime_type?.startsWith('image/')
                            const isPdf = att.mime_type === 'application/pdf'
                            return isImage ? (
                              <button key={att.id} onClick={() => setLightboxUrl(att.file_url)} className="block w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:border-brand-400 transition-colors">
                                <img src={att.file_url} alt={att.file_name} className="w-full h-full object-cover" />
                              </button>
                            ) : isPdf ? (
                              <button key={att.id} onClick={() => setPdfUrl(att.file_url)}
                                className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-400 transition-colors">
                                <FileText className="w-4 h-4 text-error-400 flex-shrink-0" />
                                <span className="text-xs text-gray-700 truncate max-w-[120px]">{att.file_name}</span>
                              </button>
                            ) : (
                              <a key={att.id} href={att.file_url} download
                                className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-400 transition-colors">
                                <Download className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="text-xs text-gray-700 truncate max-w-[120px]">{att.file_name}</span>
                              </a>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Campanhas */}
      {activeTab === 'campanhas' && (
        <Card>
          {campaigns.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12">Não participou de nenhuma campanha</p>
          ) : (
            <div className="space-y-3">
              {campaigns.map(c => (
                <div key={c.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', c.is_active_campaign ? 'bg-brand-50 text-brand-500' : 'bg-gray-100 text-gray-400')}>
                      <Flag className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-800">{c.name}</span>
                        {c.is_active_campaign && <span className="text-[10px] font-bold text-brand-500 bg-brand-50 px-1.5 py-0.5 rounded">ATIVA</span>}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {c.election_year} &middot; Engajado em {formatDate(c.engaged_at)} &middot; {c.status_label}
                      </p>
                    </div>
                  </div>
                  <Badge variant={c.engagement_active ? 'success' : 'gray'} size="sm" dot>
                    {c.engagement_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* SlidePanel de edição */}
      <SlidePanel open={editOpen} onClose={() => setEditOpen(false)} title="Editar dados">
        <div className="p-6 space-y-4">
          <Input label="Nome" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
          <Input label="Apelido" value={editForm.nickname} onChange={e => setEditForm(f => ({ ...f, nickname: e.target.value }))} />
          <Input label="Telefone" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
          <Input label="Email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} />
          <Input label="CPF" value={editForm.cpf} onChange={e => setEditForm(f => ({ ...f, cpf: e.target.value }))} />
          <Input label="Data de nascimento" type="date" value={editForm.birth_date} onChange={e => setEditForm(f => ({ ...f, birth_date: e.target.value }))} />
          <Select label="Gênero" value={editForm.gender} onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))} options={[{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Feminino' }, { value: 'O', label: 'Outro' }]} placeholder="Selecione" />

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Endereço</p>
            <div className="space-y-4">
              <Input label="CEP" value={editForm.zip_code} onChange={e => setEditForm(f => ({ ...f, zip_code: e.target.value }))} />
              <Input label="Rua" value={editForm.street} onChange={e => setEditForm(f => ({ ...f, street: e.target.value }))} />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Número" value={editForm.number} onChange={e => setEditForm(f => ({ ...f, number: e.target.value }))} />
                <Input label="Bairro" value={editForm.neighborhood} onChange={e => setEditForm(f => ({ ...f, neighborhood: e.target.value }))} />
              </div>
              <Input label="Complemento" value={editForm.complement} onChange={e => setEditForm(f => ({ ...f, complement: e.target.value }))} />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Dados eleitorais</p>
            <div className="grid grid-cols-3 gap-4">
              <Input label="Título" value={editForm.voter_registration} onChange={e => setEditForm(f => ({ ...f, voter_registration: e.target.value }))} />
              <Input label="Zona" value={editForm.electoral_zone} onChange={e => setEditForm(f => ({ ...f, electoral_zone: e.target.value }))} />
              <Input label="Seção" value={editForm.electoral_section} onChange={e => setEditForm(f => ({ ...f, electoral_section: e.target.value }))} />
            </div>
          </div>

          <Textarea label="Observações" value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows={3} />

          <div className="pt-4 flex gap-3">
            <Button onClick={handleSave} loading={saving} className="flex-1">Salvar</Button>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="flex-1">Cancelar</Button>
          </div>
        </div>
      </SlidePanel>

      {/* Modal de exclusão */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setDeleteModalOpen(false)}>Desativar {voter.is_voter ? 'eleitor' : 'contato'}</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja desativar <strong>{voter.name}</strong>? O registro não será excluído permanentemente.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDelete} loading={deleting}>Desativar</Button>
        </ModalFooter>
      </Modal>

      <RegisterActivityModal
        open={activityModalOpen}
        onClose={() => setActivityModalOpen(false)}
        onSuccess={fetchVoter}
        voter={voter ? { id: voter.id, name: voter.name, phone: voter.phone } : null}
      />

      {/* Lightbox imagem */}
      {lightboxUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={() => setLightboxUrl(null)}>
          <button onClick={() => setLightboxUrl(null)} className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">
            <X className="w-6 h-6" />
          </button>
          <img src={lightboxUrl} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Viewer PDF */}
      {pdfUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/80" onClick={() => setPdfUrl(null)}>
          <div className="flex items-center justify-between px-4 py-3">
            <a href={pdfUrl} download className="text-sm text-white/70 hover:text-white flex items-center gap-2">
              <Download className="w-4 h-4" /> Baixar
            </a>
            <button onClick={() => setPdfUrl(null)} className="h-10 w-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 px-4 pb-4" onClick={e => e.stopPropagation()}>
            <iframe src={pdfUrl} className="w-full h-full rounded-lg bg-white" />
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className={clsx('text-sm', muted ? 'text-gray-300' : 'text-gray-900')}>{value}</p>
    </div>
  )
}
